import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, Dna, RotateCcw, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div
        className={`max-w-[85%] sm:max-w-[78%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "text-black rounded-tr-sm"
            : "bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-tl-sm"
        }`}
        style={isUser ? { background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' } : undefined}
        data-testid={isUser ? "msg-user" : "msg-assistant"}
      >
        {msg.content}
      </div>
    </div>
  );
}

export default function BrandDnaPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [todayUsed, setTodayUsed] = useState(0);
  const [todayLimit, setTodayLimit] = useState(30);
  const [isComplete, setIsComplete] = useState(false);
  const [summary, setSummary] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/brand-dna/chat`);
        setMessages(data.messages || []);
        setTodayUsed(data.messages_today || 0);
        setIsComplete(data.is_complete || false);
        setSummary(data.summary || null);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Brand DNA konnte nicht geladen werden");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text, ts: new Date().toISOString() }]);
    try {
      const { data } = await axios.post(`${API}/brand-dna/chat`, { message: text });
      setMessages(prev => [...prev, data.assistant_message]);
      setTodayUsed(data.messages_today);
      setTodayLimit(data.messages_today_limit);
      setIsComplete(data.is_complete);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Antwort fehlgeschlagen");
      // remove optimistic user msg if request crashed
      setMessages(prev => prev.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    setShowResetConfirm(false);
    try {
      await axios.post(`${API}/brand-dna/reset`);
      const { data } = await axios.get(`${API}/brand-dna/chat`);
      setMessages(data.messages || []);
      setTodayUsed(data.messages_today || 0);
      setIsComplete(false);
      setSummary(null);
      toast.success("Chat zurückgesetzt — wir starten neu");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset fehlgeschlagen"); }
  };

  const summarize = async () => {
    try {
      const { data } = await axios.post(`${API}/brand-dna/summarize`);
      setSummary(data.summary);
      toast.success("Brand DNA Zusammenfassung erstellt");
    } catch (e) { toast.error(e.response?.data?.detail || "Zusammenfassung fehlgeschlagen"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#09090b]" data-testid="brand-dna-page">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-zinc-800/60 bg-black/40 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="h-8 w-8 text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }}>
            <Dna className="w-4 h-4 text-black" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-sm font-semibold text-white truncate" style={{ fontFamily: 'Outfit' }}>Brand DNA</h1>
            <p className="text-[10px] text-zinc-500">Marken-Identität & Angebot in 10 Schritten · GPT-5.2</p>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="hidden sm:inline-flex text-[10px] text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1" data-testid="dna-quota">
              {todayUsed}/{todayLimit} heute
            </span>
            {isComplete && !summary && (
              <button onClick={summarize} className="text-[10px] font-semibold px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25" data-testid="dna-summarize-btn">
                <Sparkles className="w-3 h-3 inline mr-1" /> Speichern
              </button>
            )}
            <button onClick={() => setShowResetConfirm(true)} className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800" title="Chat neu starten" data-testid="dna-reset-btn">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* Summary banner if available */}
      {summary && (
        <div className="flex-shrink-0 bg-cyan-500/5 border-b border-cyan-500/20" data-testid="dna-summary-banner">
          <div className="max-w-3xl mx-auto px-4 py-2.5 text-[11px] text-zinc-400">
            <span className="text-cyan-300 font-semibold mr-2">DNA gespeichert:</span>
            {summary.niche} · Zielgruppe: {summary.audience}
          </div>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="dna-messages">
        <div className="max-w-3xl mx-auto px-4 py-6">
          {messages.map((m, i) => <MessageBubble key={i} msg={m} />)}
          {sending && (
            <div className="flex justify-start mb-3">
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 text-cyan-400 animate-spin" />
                <span className="text-xs text-zinc-400">denkt nach…</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="flex-shrink-0 border-t border-zinc-800/60 bg-black/40 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 py-3">
          {isComplete && (
            <div className="mb-2 p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
              <span className="text-[11px] text-cyan-200">Deine Brand DNA ist vollständig — du kannst die Zusammenfassung speichern oder weiterchatten.</span>
            </div>
          )}
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={messages.length <= 1 ? "Beantworte die zwei Fragen oben…" : "Deine Antwort (Enter zum Senden, Shift+Enter für neue Zeile)…"}
              className="min-h-[52px] max-h-[200px] resize-none bg-zinc-900 border-zinc-800 text-white text-sm placeholder:text-zinc-600 focus:border-cyan-500/40"
              disabled={sending || todayUsed >= todayLimit}
              data-testid="dna-input"
            />
            <Button
              onClick={send}
              disabled={!input.trim() || sending || todayUsed >= todayLimit}
              className="text-black h-[52px] px-4 flex-shrink-0 font-semibold"
              style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }}
              data-testid="dna-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          {todayUsed >= todayLimit && (
            <p className="text-[10px] text-amber-400 mt-1.5">
              Tageslimit erreicht ({todayLimit} Nachrichten). Komm morgen wieder, dann geht's weiter.
            </p>
          )}
        </div>
      </div>

      {/* Reset confirm */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-sm" data-testid="dna-reset-dialog">
          <DialogHeader>
            <DialogTitle>Chat neu starten?</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Der gesamte Brand-DNA-Verlauf wird gelöscht und du beginnst wieder bei den zwei Einstiegsfragen.
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setShowResetConfirm(false)} className="text-zinc-400">Abbrechen</Button>
            <Button onClick={reset} className="text-black font-semibold" style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }} data-testid="dna-reset-confirm-btn">Ja, neu starten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
